import React from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import ExtractedTicketCard from "./ExtractedTicketCard";

const TicketReviewStep = ({ tickets, setTickets, duplicateInfo, columns }) => {
  const handleUpdateTicket = (index, updatedTicket) => {
    const newTickets = [...tickets];
    newTickets[index] = updatedTicket;
    setTickets(newTickets);
  };

  const handleRemoveTicket = (index) => {
    const newTickets = tickets.filter((_, i) => i !== index);
    setTickets(newTickets);
  };

  const validTickets = tickets.filter((ticket) => ticket.title.trim().length > 0);
  const invalidTickets = tickets.filter((ticket) => ticket.title.trim().length === 0);

  return (
    <div className="space-y-6 transition-colors duration-200">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Review Extracted Tickets
        </h3>
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Review and edit the tickets extracted by AI. You can modify the title,
          description, priority, or remove unwanted items.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 transition-colors">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-900 dark:text-green-200">{validTickets.length}</p>
              <p className="text-xs text-green-700 dark:text-green-400/80">Ready to Create</p>
            </div>
          </div>
        </div>

        {invalidTickets.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 transition-colors">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <div>
                <p className="text-2xl font-bold text-red-900 dark:text-red-200">
                  {invalidTickets.length}
                </p>
                <p className="text-xs text-red-700 dark:text-red-400/80">Missing Title</p>
              </div>
            </div>
          </div>
        )}

        {duplicateInfo && duplicateInfo.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 transition-colors">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
                  {duplicateInfo.length}
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400/80">Duplicates Skipped</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Duplicates Info */}
      {duplicateInfo && duplicateInfo.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 transition-colors">
          <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
            Duplicate Tickets (Automatically Skipped)
          </h4>
          <ul className="text-sm text-yellow-800 dark:text-yellow-300/80 space-y-1">
            {duplicateInfo.map((dup, index) => (
              <li key={index}>
                • {dup.title} - {dup.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tickets Grid */}
      {tickets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-slate-900/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-700 transition-colors">
          <AlertCircle className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-slate-400 font-medium">No tickets to review</p>
          <p className="text-sm text-gray-500 dark:text-slate-500 mt-1">
            All extracted tickets were duplicates or invalid
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
          {tickets.map((ticket, index) => (
            <ExtractedTicketCard
              key={index}
              ticket={ticket}
              index={index}
              onUpdate={handleUpdateTicket}
              onRemove={handleRemoveTicket}
              columns={columns}
            />
          ))}
        </div>
      )}

      {/* Validation Warning */}
      {invalidTickets.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start space-x-3 transition-colors">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 dark:text-red-200">Validation Required</p>
            <p className="text-sm text-red-700 dark:text-red-300/80">
              {invalidTickets.length} ticket(s) are missing required titles.
              Please add titles or remove these tickets before creating cards.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketReviewStep;
